import { Capacitor, registerPlugin } from "@capacitor/core";

export const LANGUAGE_STORAGE_KEY = "kontrata_lang";
export const LEGACY_ANALYTICS_OPT_OUT_KEY = "kp_analytics_opt_out";
export const LEGACY_NATIVE_ANALYTICS_OPT_OUT_KEY = `${LEGACY_ANALYTICS_OPT_OUT_KEY}_native`;

type RestoreLanguage = "en" | "tl";
type RestoreSettings = { language: RestoreLanguage | null; analyticsOptOut: boolean };
type RestoreUpdate = { language?: RestoreLanguage; analyticsOptOut?: true };
interface RestoreSettingsPlugin {
  read(): Promise<RestoreSettings>;
  update(input: RestoreUpdate): Promise<void>;
}

const restorePlugin = registerPlugin<RestoreSettingsPlugin>("RestoreSettings");
let language: RestoreLanguage | null = null;
let analyticsOptOut = false;
let privacyReady = false;
let settingsQueue: Promise<void> = Promise.resolve();
let initialization: Promise<void> | null = null;

function isAndroid(): boolean {
  return Capacitor.getPlatform() === "android";
}

function isLanguage(value: unknown): value is RestoreLanguage {
  return value === "en" || value === "tl";
}

function validSettings(value: unknown): value is RestoreSettings {
  return typeof value === "object" && value !== null &&
    "language" in value && (value.language === null || isLanguage(value.language)) &&
    "analyticsOptOut" in value && typeof value.analyticsOptOut === "boolean" &&
    Object.keys(value).every(key => key === "language" || key === "analyticsOptOut");
}

// Capacitor's bridge and process cache are shared; serialize migration and writes.
function serialize(operation: () => Promise<void>): Promise<void> {
  const pending = settingsQueue.then(operation);
  settingsQueue = pending.catch(() => {});
  return pending;
}

function currentChoices(): RestoreUpdate {
  return { ...(language ? { language } : {}), ...(analyticsOptOut ? { analyticsOptOut: true } : {}) };
}

function browserChoices(): RestoreSettings & { available: boolean } {
  try {
    const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return {
      language: isLanguage(storedLanguage) ? storedLanguage : null,
      analyticsOptOut: localStorage.getItem(LEGACY_ANALYTICS_OPT_OUT_KEY) === "true",
      available: true,
    };
  } catch {
    return { language: null, analyticsOptOut: false, available: false };
  }
}

/** Hydrate before React mounts. Only validated scalar choices cross stores. */
export function initializeAndroidRestoreSettings(): Promise<void> {
  if (!isAndroid()) return Promise.resolve();
  if (initialization) return initialization;
  privacyReady = false;
  initialization = serialize(async () => {
    try {
      const stored: unknown = await restorePlugin.read();
      if (!validSettings(stored)) throw new Error("Invalid restore settings response");
      // A user choice made during this read wins over its older snapshot.
      language ??= stored.language;
      analyticsOptOut ||= stored.analyticsOptOut;

      const { Preferences } = await import("@capacitor/preferences");
      const legacyOptOut = await Preferences.get({ key: LEGACY_NATIVE_ANALYTICS_OPT_OUT_KEY });
      analyticsOptOut ||= legacyOptOut.value === "true";
      const browser = browserChoices();
      language ??= browser.language;
      analyticsOptOut ||= browser.analyticsOptOut;

      if (analyticsOptOut) {
        // Keep existing UI/retry consumers aware; identifiers stay on this device.
        await Preferences.set({ key: LEGACY_NATIVE_ANALYTICS_OPT_OUT_KEY, value: "true" }).catch(() => {});
        try { localStorage.setItem(LEGACY_ANALYTICS_OPT_OUT_KEY, "true"); } catch { /* the process cache still stops collection */ }
      }
      await restorePlugin.update(currentChoices());
      privacyReady = browser.available;
    } catch {
      // Unknown privacy state stops collection, while login/UI startup can continue.
      privacyReady = false;
    }
  }).finally(() => { initialization = null; });
  return initialization;
}

/** Synchronous reads let the first React render respect restored choices. */
export function androidRestoreLanguage(): RestoreLanguage | null {
  return isAndroid() ? language : null;
}

/** A failed/uninitialized Android read never authorizes analytics collection. */
export function androidAnalyticsBlocked(): boolean {
  return isAndroid() && (!privacyReady || analyticsOptOut);
}

/** UI changes apply immediately; callers handle unavailable durable storage. */
export function persistAndroidRestoreLanguage(value: RestoreLanguage): Promise<void> {
  if (!isAndroid()) return Promise.resolve();
  if (!isLanguage(value)) return Promise.reject(new Error("Invalid restore language"));
  language = value;
  return serialize(() => restorePlugin.update(currentChoices()));
}

/** Stop locally before asynchronous persistence or server-cleanup work begins. */
export function persistAndroidAnalyticsOptOut(): Promise<void> {
  if (!isAndroid()) return Promise.resolve();
  analyticsOptOut = true;
  return serialize(() => restorePlugin.update(currentChoices()));
}
